/**
 * Utility functions for creating URL-friendly slugs
 */

/**
 * Converts a string to a URL-friendly slug
 * @param text - The text to convert to a slug
 * @returns A URL-friendly slug
 */
export function createSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[\s\W-]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '')
}

/**
 * Creates a unique slug for a surf spot
 * @param name - The surf spot name
 * @param location - The surf spot location
 * @param country - The surf spot country
 * @returns A unique slug combining name, location, and country
 */
export function createSurfSpotSlug(name: string, location: string, country: string): string {
  // Combine name, location, and country for uniqueness
  const combined = `${name} ${location} ${country}`
  return createSlug(combined)
}

/**
 * Creates a slug from just the name (for simpler URLs)
 * @param name - The surf spot name
 * @returns A slug from the name only
 */
export function createNameSlug(name: string): string {
  return createSlug(name)
}

/**
 * Validates if a string is a valid slug format
 * @param slug - The slug to validate
 * @returns True if the slug is valid
 */
export function isValidSlug(slug: string): boolean {
  // Slug should only contain lowercase letters, numbers, and hyphens
  // Should not start or end with hyphens
  const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
  return slugRegex.test(slug)
}
